"use client"

import { RichText, RichTextValue } from "@/components/rich-text"
import { SanityImageAsset, SanityReference } from "sanity-codegen"
import { motion, useInView, useScroll, useTransform } from "motion/react"
import { ComponentRef, useRef } from "react"
import SanityImageField from "@/integrations/sanity/components/SanityImageField"
import { cn } from "@/utils/cn"

interface GalleryProps {
  content: {
    topMarquee: string
    bottomMarquee: string
    mainImage: { asset: SanityReference<SanityImageAsset> }
    title: RichTextValue
    description: string
    gallery: { asset: SanityReference<SanityImageAsset> }[]
  }
}

export function Gallery({ content }: GalleryProps) {
  const containerRef = useRef<ComponentRef<"div">>(null)
  const topGalleryRef = useRef<ComponentRef<"div">>(null)
  const { scrollYProgress } = useScroll({
    target: topGalleryRef,
    offset: ["start start", "end end"],
    axis: "y",
  })
  const scale = useTransform(scrollYProgress, [1, 0], [1, 0.7])
  const radius = useTransform(scrollYProgress, [1, 0], [0, 32])
  const inView = useInView(containerRef, { once: true })
  const minHeight = useTransform(scrollYProgress, [1, 0], ["0vh", "100vh"])

  const mix = useTransform(scrollYProgress, (v) => {
    return `${100 - v * 100}%`
  })
  /**
   * Split images array into an array of 3 array each
   */
  const imagesChunks = content.gallery.reduce(
    (chunks, item, index) => {
      const chunkIndex = Math.floor(index / 3)
      if (!chunks[chunkIndex]) {
        chunks[chunkIndex] = []
      }
      chunks[chunkIndex].push(item)
      return chunks
    },
    [] as { asset: SanityReference<SanityImageAsset> }[][]
  )

  return (
    <div ref={containerRef} className="relative min-h-[200vh] bg-neutral-light">
      <div className="sticky top-0 flex min-h-screen flex-col justify-center">
        <motion.div
          initial={{
            opacity: 0,
            y: 100,
          }}
          animate={{
            opacity: inView ? 1 : 0,
            y: inView ? 0 : 100,
          }}
          transition={{ type: "spring", duration: 0.3 }}
          className="overflow-hidden"
        >
          <Marquee direction="left" text={content.topMarquee} />
        </motion.div>
        <div ref={topGalleryRef} />
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: inView ? "auto" : 0, opacity: inView ? 1 : 0 }}
          transition={{ duration: 0.3, delay: 2 }}
          style={
            {
              "--mix-bg": mix,
            } as React.CSSProperties
          }
          className="overflow-hidden [background-color:color-mix(in_srgb,var(--color-neutral-light)_calc(100%-var(--mix-bg)),var(--color-primary-800)_var(--mix-bg))]"
        >
          <motion.div
            style={{ scale, borderRadius: radius }}
            className="overflow-hidden mt-6 md:mt-0"
          >
            <SanityImageField
              asset={content.mainImage}
              className="object-cover"
            />
          </motion.div>
          <motion.div
            style={{ minHeight }}
            className="px-4 md:px-16 lg:px-20 xl:px-35"
          >
            <RichText
              className="mt-6 md:mt-0 relative -top-2 text-primary-500"
              billboardClassName="text-neutral-light"
              value={content.title}
            />
            <p className="pt-6 pb-14 max-w-[60rem] font-family-gthaptik text-lg font-light text-neutral-light md:pb-0 xl:text-xlg">
              {content.description}
            </p>
            <div>
              {imagesChunks.map((chunk, i) => (
                <div
                  key={i}
                  className="relative my-10 h-[30rem] md:my-20 md:h-[45rem] lg:h-[90rem]"
                >
                  {chunk.map((image, j) => (
                    <motion.div
                      key={j}
                      initial={{ opacity: 0, scale: 0.6 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{
                        type: "spring",
                        duration: 0.5,
                        delay: j * 0.3,
                      }}
                      viewport={{ once: true }}
                      className={cn(
                        "absolute overflow-hidden rounded-lg object-cover",
                        j === 0 &&
                          "top-8 left-0 h-[24rem] w-[16rem] md:h-[33rem] md:w-[22rem] lg:h-[66rem] lg:w-[44rem]",
                        j === 1 &&
                          "top-1/2 left-1/2 z-10 h-[18rem] w-[14rem] -translate-x-1/2 md:h-[25rem] md:w-[16rem] lg:h-[50rem] lg:w-[33rem]",
                        j === 2 &&
                          "-top-10 left-1/2 h-[27rem] w-[19rem] md:-top-20 md:h-[41rem] md:w-[27rem] lg:h-[83rem] lg:w-[55rem]"
                      )}
                    >
                      <SanityImageField asset={image} />
                    </motion.div>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
        <motion.div
          initial={{
            opacity: 0,
            y: 100,
          }}
          animate={{
            opacity: inView ? 1 : 0,
            y: inView ? 0 : 100,
          }}
          transition={{ type: "spring", duration: 0.3 }}
          className="overflow-hidden"
        >
          <Marquee direction="right" text={content.bottomMarquee} />
        </motion.div>
      </div>
    </div>
  )
}

function Marquee({
  direction,
  text,
}: {
  direction: "left" | "right"
  text: string
}) {
  return (
    <motion.div
      initial={{
        translateX: direction === "left" ? 0 : "-50%",
        translateY: "100%",
        opacity: 0,
      }}
      whileInView={{
        translateY: "0",
        opacity: 1,
      }}
      viewport={{ once: true }}
      animate={{
        translateX: direction === "left" ? "-50%" : 0,
      }}
      transition={{
        opacity: { type: "spring", duration: 0.5 },
        translateY: { type: "spring", duration: 0.5 },
        translateX: {
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        },
      }}
      className="flex min-w-max gap-20 text-7xl font-bold text-viola-200 md:text-7xl md:text-10xl"
    >
      <div>{text}</div>
      <div>{text}</div>
    </motion.div>
  )
}
